/**
 * Barra de etapas (widget `status`).
 *
 * Es el semaforo de la cabecera de un formulario: se ven TODAS las etapas del
 * circuito y cual esta activa, sin abrir nada. La diferencia con `badge` es esa
 * — la pastilla dice donde estas, la barra dice donde estas DENTRO DE QUE.
 *
 * Un periodo de expensas es el caso tipico: borrador, calculado, emitido,
 * cerrado. Con un desplegable se ve una palabra; con la barra se ve
 * que le faltan dos pasos y cuales son.
 *
 * Dos decisiones que definen el componente:
 *
 * 1. **Casi siempre es de solo lectura.** Las etapas las mueven las acciones de
 *    servidor (liquidar, emitir, cerrar), no el usuario eligiendo de una lista:
 *    saltar de "borrador" a "cerrado" a mano dejaria un periodo sin calcular.
 *    Cuando el campo es editable, cada etapa es un boton de un `radiogroup` real.
 * 2. **El color no es el unico portador de informacion** (WCAG 1.4.1): la etapa
 *    activa ademas se marca con `aria-current`, se dibuja rellena y las ya
 *    recorridas se atenuan. Quien no distingue los colores igual ve el avance.
 */

import clsx from 'clsx';
import { useRef } from 'react';

import { stateTone } from '@/components/fields/FieldDisplay';
import { SelectionField } from '@/components/fields/SelectionField';
import type { FieldInputProps } from '@/components/fields/types';
import type { JsonValue } from '@/lib/types';

/** Mas etapas que esto y la barra no entra en una pantalla de celular. */
const MAXIMO_ETAPAS = 6;

const CLASES_ACTIVA: Record<string, string> = {
  neutral: 'border-line-strong bg-surface-raised text-fg',
  accent: 'border-accent bg-accent text-accent-contrast',
  success: 'border-success bg-success-soft text-success',
  warning: 'border-warning bg-warning-soft text-warning',
  danger: 'border-danger bg-danger-soft text-danger',
};

/** Clase de una etapa que no es la actual. Las ya recorridas se ven distintas de las futuras. */
function clasePasiva(recorrida: boolean): string {
  return recorrida
    ? 'border-line bg-surface text-fg-muted'
    : 'border-dashed border-line bg-transparent text-fg-faint';
}

export function StatusBarField(props: FieldInputProps) {
  const { id, meta, value, onChange, readonly, invalid, describedBy } = props;
  const contenedor = useRef<HTMLDivElement>(null);
  const etapas = meta.choices ?? [];

  // Sin etapas declaradas no hay circuito que dibujar; con demasiadas, la lista
  // cerrada es mas honesta que una barra que se corta.
  if (etapas.length === 0 || etapas.length > MAXIMO_ETAPAS) {
    return <SelectionField {...props} />;
  }

  const posicionActual = etapas.findIndex((etapa) => etapa.value === value);

  const mover = (delta: number): void => {
    const siguiente = (posicionActual + delta + etapas.length) % etapas.length;
    const destino = etapas[siguiente];
    if (destino === undefined) return;
    onChange(destino.value);
    // El foco sigue a la seleccion, como manda el patron de radiogroup.
    contenedor.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[siguiente]?.focus();
  };

  const contenido = etapas.map((etapa, posicion) => {
    const activa = etapa.value === value;
    const recorrida = posicionActual >= 0 && posicion < posicionActual;
    const tono = stateTone(meta, etapa.value as JsonValue);
    const clases = clsx(
      'inline-flex items-center whitespace-nowrap rounded-full border px-3 py-1 text-sm font-medium transition-colors',
      activa ? (CLASES_ACTIVA[tono] ?? CLASES_ACTIVA.neutral) : clasePasiva(recorrida),
    );

    if (readonly) {
      return (
        <span key={String(etapa.value)} aria-current={activa ? 'step' : undefined} className={clases}>
          {etapa.label}
        </span>
      );
    }

    return (
      <button
        key={String(etapa.value)}
        type="button"
        role="radio"
        aria-checked={activa}
        tabIndex={activa || (posicionActual < 0 && posicion === 0) ? 0 : -1}
        onClick={() => {
          onChange(etapa.value);
        }}
        // El hover solo sobre las que no son la actual: teñir el borde de la
        // activa le taparía el tono que dice en qué estado está el registro.
        className={clsx('touch-target', clases, !activa && 'hover:border-line-strong hover:text-fg')}
      >
        {etapa.label}
      </button>
    );
  });

  if (readonly) {
    return (
      <div
        id={id}
        role="group"
        aria-labelledby={`${id}-label`}
        aria-describedby={describedBy}
        className="flex flex-wrap items-center gap-1.5"
      >
        {contenido}
      </div>
    );
  }

  return (
    <div
      ref={contenedor}
      id={id}
      role="radiogroup"
      aria-labelledby={`${id}-label`}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className="flex flex-wrap items-center gap-1.5"
      onKeyDown={(evento) => {
        if (evento.key === 'ArrowRight' || evento.key === 'ArrowDown') {
          evento.preventDefault();
          mover(1);
        } else if (evento.key === 'ArrowLeft' || evento.key === 'ArrowUp') {
          evento.preventDefault();
          mover(-1);
        }
      }}
    >
      {contenido}
    </div>
  );
}
